import { useMemo, useState } from 'react';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WELL_COLORS, ANALYTE_COLORS, CHART_STYLES } from '../../chartTheme';
interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
  // Optional overrides so the same chart serves the phreatic water-level view.
  categoryLabel?: string;    // column-3 header (default "Analytes")
  valueUnit?: string;        // tooltip unit (default "mg/L")
  glassSections?: boolean;   // draw glass-cyan containers behind column-3 groups
}

function convertConc(val: number | null, unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

/* ── Types ────────────────────────────────────────────────────────────── */

interface SankeyNode {
  id: string;
  label: string;
  color: string;
  column: number; // 0=site, 1=well, 2=analyte
  x: number;
  y: number;
  height: number;
}

interface SankeyLink {
  source: string;
  target: string;
  value: number;
  sourceNode: SankeyNode;
  targetNode: SankeyNode;
  sourceColor: string;
  // tooltip info
  siteName: string;
  wellName: string;
  analyteName: string;
  concentration: number;
}

/* ── Layout constants ─────────────────────────────────────────────────── */

const SVG_WIDTH = 800;
const SVG_HEIGHT = 400;
const PADDING = { top: 20, right: 120, bottom: 20, left: 120 };
const NODE_WIDTH = 14;
const NODE_GAP = 8;
const GROUP_GAP = 30; // extra vertical space between column-3 metric sections (glass boxes)

const COLUMN_X = [
  PADDING.left,
  SVG_WIDTH / 2 - NODE_WIDTH / 2,
  SVG_WIDTH - PADDING.right - NODE_WIDTH,
];

/* ── Cubic bezier link path ───────────────────────────────────────────── */

function linkPath(
  sx: number,
  sy0: number,
  sy1: number,
  tx: number,
  ty0: number,
  ty1: number,
): string {
  const midX = (sx + tx) / 2;
  return [
    `M ${sx},${sy0}`,
    `C ${midX},${sy0} ${midX},${ty0} ${tx},${ty0}`,
    `L ${tx},${ty1}`,
    `C ${midX},${ty1} ${midX},${sy1} ${sx},${sy1}`,
    'Z',
  ].join(' ');
}

/* ── Component ────────────────────────────────────────────────────────── */

export function SankeyDiagram({ data, unit, categoryLabel = 'Analytes', valueUnit = 'mg/L', glassSections = false }: Props) {
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const { nodes, links, isEmpty } = useMemo(() => {
    if (!data?.wells?.length) {
      return { nodes: [] as SankeyNode[], links: [] as SankeyLink[], isEmpty: true };
    }

    // Collect unique sites, wells, analytes with latest concentration
    const siteMap = new Map<string, { wells: Map<string, Map<string, number>> }>();

    for (const well of data.wells) {
      const siteName = well.site_name || 'Unknown Site';
      if (!siteMap.has(siteName)) {
        siteMap.set(siteName, { wells: new Map() });
      }
      const site = siteMap.get(siteName)!;

      const wellName = well.well_name || well.well_id;
      if (!site.wells.has(wellName)) {
        site.wells.set(wellName, new Map());
      }
      const wellAnalytes = site.wells.get(wellName)!;

      for (const [analyteName, analyte] of Object.entries(well.analytes)) {
        const measurements = analyte.measurements || [];
        // Get the latest non-null measurement
        for (let i = measurements.length - 1; i >= 0; i--) {
          const conc = convertConc(measurements[i].concentration, unit);
          if (conc !== null && conc > 0) {
            const existing = wellAnalytes.get(analyteName) ?? 0;
            wellAnalytes.set(analyteName, Math.max(existing, conc));
            break;
          }
        }
      }
    }

    // Build flat link records
    const rawLinks: { site: string; well: string; analyte: string; value: number }[] = [];
    for (const [siteName, site] of siteMap) {
      for (const [wellName, analytes] of site.wells) {
        for (const [analyteName, value] of analytes) {
          if (value > 0) {
            rawLinks.push({ site: siteName, well: wellName, analyte: analyteName, value });
          }
        }
      }
    }

    if (rawLinks.length === 0) {
      return { nodes: [] as SankeyNode[], links: [] as SankeyLink[], isEmpty: true };
    }

    // Unique names per column
    const siteNames = [...new Set(rawLinks.map((l) => l.site))];
    const wellNames = [...new Set(rawLinks.map((l) => l.well))];
    // Natural sort groups the column into clean sections (e.g. phreatic's
    // DTW / GW / Sat bands) and orders ranges numerically (20–40 before 100–120).
    const analyteNames = [...new Set(rawLinks.map((l) => l.analyte))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
    );

    // Compute totals per node for height proportioning
    const siteTotals = new Map<string, number>();
    const wellTotals = new Map<string, number>();
    const analyteTotals = new Map<string, number>();

    for (const l of rawLinks) {
      siteTotals.set(l.site, (siteTotals.get(l.site) ?? 0) + l.value);
      wellTotals.set(l.well, (wellTotals.get(l.well) ?? 0) + l.value);
      analyteTotals.set(l.analyte, (analyteTotals.get(l.analyte) ?? 0) + l.value);
    }

    // Layout helper: position nodes vertically proportional to their total
    const availableHeight = SVG_HEIGHT - PADDING.top - PADDING.bottom;

    function layoutColumn(
      names: string[],
      totals: Map<string, number>,
      colIndex: number,
      colorPalette: readonly string[],
      paletteOffset: number,
      groupOf?: (name: string) => string,
    ): Map<string, SankeyNode> {
      const totalSum = names.reduce((s, n) => s + (totals.get(n) ?? 0), 0);
      const totalGap = (names.length - 1) * NODE_GAP;
      // Extra space wherever the group changes (so the glass section boxes have
      // room and don't overlap). Subtract it from the scale height too.
      let transitions = 0;
      if (groupOf) {
        for (let i = 1; i < names.length; i++) {
          if (groupOf(names[i]) !== groupOf(names[i - 1])) transitions++;
        }
      }
      const scaleHeight = availableHeight - totalGap - transitions * GROUP_GAP;
      const minNodeHeight = 6;

      const nodeMap = new Map<string, SankeyNode>();
      let y = PADDING.top;

      for (let i = 0; i < names.length; i++) {
        const name = names[i];
        if (groupOf && i > 0 && groupOf(name) !== groupOf(names[i - 1])) {
          y += GROUP_GAP;
        }
        const fraction = totalSum > 0 ? (totals.get(name) ?? 0) / totalSum : 1 / names.length;
        const height = Math.max(minNodeHeight, fraction * scaleHeight);

        nodeMap.set(name, {
          id: `${colIndex}-${name}`,
          label: name,
          color: colorPalette[(i + paletteOffset) % colorPalette.length],
          column: colIndex,
          x: COLUMN_X[colIndex],
          y,
          height,
        });
        y += height + NODE_GAP;
      }
      return nodeMap;
    }

    // Phreatic column-3 is grouped by metric prefix (Sat / DTW / GW Elev) so the
    // glass section boxes get spacing between them.
    const sectionGroupOf = glassSections
      ? (n: string) => n.replace(/\s*\d.*$/, '').trim() || n
      : undefined;

    const siteNodes = layoutColumn(siteNames, siteTotals, 0, WELL_COLORS, 0);
    const wellNodes = layoutColumn(wellNames, wellTotals, 1, WELL_COLORS, 0);
    const analyteNodes = layoutColumn(analyteNames, analyteTotals, 2, ANALYTE_COLORS, 0, sectionGroupOf);

    const allNodes = [
      ...siteNodes.values(),
      ...wellNodes.values(),
      ...analyteNodes.values(),
    ];

    // Build links with vertical offsets per node
    // We need to track how much "vertical space" has been consumed at each node port
    const sourceOffsets = new Map<string, number>(); // node id -> current y offset from top
    const targetOffsets = new Map<string, number>();

    // Site->Well links
    const siteToWell: SankeyLink[] = [];
    // Group by site so links from the same site are adjacent
    for (const siteName of siteNames) {
      const siteNode = siteNodes.get(siteName)!;
      const siteTotal = siteTotals.get(siteName) ?? 1;

      for (const wellName of wellNames) {
        // Sum of all analyte concentrations for this site->well pair
        const pairValue = rawLinks
          .filter((l) => l.site === siteName && l.well === wellName)
          .reduce((s, l) => s + l.value, 0);
        if (pairValue === 0) continue;

        const wellNode = wellNodes.get(wellName)!;
        const wellTotal = wellTotals.get(wellName) ?? 1;

        const srcOff = sourceOffsets.get(siteNode.id) ?? 0;
        const tgtOff = targetOffsets.get(wellNode.id) ?? 0;

        const srcHeight = (pairValue / siteTotal) * siteNode.height;
        const tgtHeight = (pairValue / wellTotal) * wellNode.height;

        siteToWell.push({
          source: siteNode.id,
          target: wellNode.id,
          value: pairValue,
          sourceNode: { ...siteNode, y: siteNode.y + srcOff, height: srcHeight },
          targetNode: { ...wellNode, y: wellNode.y + tgtOff, height: tgtHeight },
          sourceColor: siteNode.color,
          siteName,
          wellName,
          analyteName: '',
          concentration: pairValue,
        });

        sourceOffsets.set(siteNode.id, srcOff + srcHeight);
        targetOffsets.set(wellNode.id, tgtOff + tgtHeight);
      }
    }

    // Reset well source offsets for Well->Analyte links
    const wellSourceOffsets = new Map<string, number>();
    const analyteTargetOffsets = new Map<string, number>();

    const wellToAnalyte: SankeyLink[] = [];
    for (const wellName of wellNames) {
      const wellNode = wellNodes.get(wellName)!;
      const wellTotal = wellTotals.get(wellName) ?? 1;

      for (const analyteName of analyteNames) {
        const pairValue = rawLinks
          .filter((l) => l.well === wellName && l.analyte === analyteName)
          .reduce((s, l) => s + l.value, 0);
        if (pairValue === 0) continue;

        const analyteNode = analyteNodes.get(analyteName)!;
        const analyteTotal = analyteTotals.get(analyteName) ?? 1;

        const srcOff = wellSourceOffsets.get(wellNode.id) ?? 0;
        const tgtOff = analyteTargetOffsets.get(analyteNode.id) ?? 0;

        const srcHeight = (pairValue / wellTotal) * wellNode.height;
        const tgtHeight = (pairValue / analyteTotal) * analyteNode.height;

        // Find the site for this well-analyte pair
        const matchingSite = rawLinks.find(
          (l) => l.well === wellName && l.analyte === analyteName,
        )?.site ?? '';

        wellToAnalyte.push({
          source: wellNode.id,
          target: analyteNode.id,
          value: pairValue,
          sourceNode: { ...wellNode, y: wellNode.y + srcOff, height: srcHeight },
          targetNode: { ...analyteNode, y: analyteNode.y + tgtOff, height: tgtHeight },
          sourceColor: wellNode.color,
          siteName: matchingSite,
          wellName,
          analyteName,
          concentration: pairValue,
        });

        wellSourceOffsets.set(wellNode.id, srcOff + srcHeight);
        analyteTargetOffsets.set(analyteNode.id, tgtOff + tgtHeight);
      }
    }

    return {
      nodes: allNodes,
      links: [...siteToWell, ...wellToAnalyte],
      isEmpty: false,
    };
  }, [data, unit, glassSections]);

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center" style={{ height: 400 }}>
        <p
          className="text-xs font-medium tracking-widest uppercase"
          style={{ color: 'var(--dv-text-secondary)' }}
        >
          NO ANALYTICAL DATA AVAILABLE
        </p>
      </div>
    );
  }

  const unitLabel = valueUnit;

  // Glass-cyan section containers (phreatic): group the column-3 nodes by their
  // label prefix (e.g. "Sat", "DTW", "GW Elev") and box each group's y-extent.
  const SECTION_NAMES: Record<string, string> = {
    Sat: 'Saturated Thickness',
    DTW: 'Depth to Water',
    'GW Elev': 'GW Elevation',
  };
  const sectionBoxes = glassSections
    ? Array.from(
        nodes
          .filter((n) => n.column === 2)
          .reduce((m, n) => {
            const key = n.label.replace(/\s*\d.*$/, '').trim() || n.label;
            const cur = m.get(key) ?? { top: Infinity, bottom: -Infinity };
            cur.top = Math.min(cur.top, n.y);
            cur.bottom = Math.max(cur.bottom, n.y + n.height);
            m.set(key, cur);
            return m;
          }, new Map<string, { top: number; bottom: number }>())
          .entries(),
      ).map(([key, b]) => ({ key, top: b.top, bottom: b.bottom }))
    : [];

  return (
      <div>
        <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
          Sankey flow diagram showing contamination pathways from sources through transport mechanisms to receptors. Wider flows indicate higher contaminant mass transfer.
        </p>
        <svg
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          width="100%"
          height={400}
          style={{ overflow: 'visible' }}
        >
          {/* Glass-cyan containers clarifying the water-level sections */}
          {sectionBoxes.map((s) => (
            <g key={s.key}>
              <rect
                x={COLUMN_X[2] - 8}
                y={s.top - 16}
                width={150}
                height={s.bottom - s.top + 22}
                rx={8}
                fill="rgba(34,211,238,0.07)"
                stroke="rgba(34,211,238,0.40)"
                strokeWidth={1}
              />
              <text
                x={COLUMN_X[2] + NODE_WIDTH / 2}
                y={s.top - 5}
                textAnchor="middle"
                style={{
                  fontSize: 8,
                  fontWeight: 700,
                  fill: 'rgb(34,211,238)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {SECTION_NAMES[s.key] ?? s.key}
              </text>
            </g>
          ))}

          {/* Column headers */}
          <text
            x={COLUMN_X[0] + NODE_WIDTH / 2}
            y={10}
            textAnchor="middle"
            style={{
              fontSize: 10,
              fontWeight: 600,
              fill: 'var(--dv-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Sites
          </text>
          <text
            x={COLUMN_X[1] + NODE_WIDTH / 2}
            y={10}
            textAnchor="middle"
            style={{
              fontSize: 10,
              fontWeight: 600,
              fill: 'var(--dv-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Wells
          </text>
          <text
            x={COLUMN_X[2] + NODE_WIDTH / 2}
            y={10}
            textAnchor="middle"
            style={{
              fontSize: 10,
              fontWeight: 600,
              fill: 'var(--dv-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {categoryLabel}
          </text>

          {/* Links */}
          {links.map((link, i) => {
            const sx = link.sourceNode.x + NODE_WIDTH;
            const sy0 = link.sourceNode.y;
            const sy1 = link.sourceNode.y + link.sourceNode.height;
            const tx = link.targetNode.x;
            const ty0 = link.targetNode.y;
            const ty1 = link.targetNode.y + link.targetNode.height;
            // Highlight a link if directly hovered, OR if either end node is
            // hovered — so hovering a well lights up all its routes to the buckets.
            const isHovered =
              hoveredLink === i ||
              (hoveredNode !== null && (link.source === hoveredNode || link.target === hoveredNode));

            return (
              <path
                key={`link-${i}`}
                d={linkPath(sx, sy0, sy1, tx, ty0, ty1)}
                fill={glassSections ? 'rgb(34,211,238)' : link.sourceColor}
                fillOpacity={isHovered ? 0.55 : 0.3}
                stroke={glassSections ? 'rgb(34,211,238)' : link.sourceColor}
                strokeOpacity={isHovered ? 0.7 : 0}
                strokeWidth={isHovered ? 1 : 0}
                style={{
                  transition: `fill-opacity ${CHART_STYLES.animationDuration}ms ${CHART_STYLES.animationEasing}, stroke-opacity ${CHART_STYLES.animationDuration}ms ${CHART_STYLES.animationEasing}`,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  setHoveredLink(i);
                  const svgRect = (e.target as SVGElement).ownerSVGElement?.getBoundingClientRect();
                  if (svgRect) {
                    setTooltipPos({
                      x: e.clientX - svgRect.left,
                      y: e.clientY - svgRect.top,
                    });
                  }
                }}
                onMouseMove={(e) => {
                  const svgRect = (e.target as SVGElement).ownerSVGElement?.getBoundingClientRect();
                  if (svgRect) {
                    setTooltipPos({
                      x: e.clientX - svgRect.left,
                      y: e.clientY - svgRect.top,
                    });
                  }
                }}
                onMouseLeave={() => {
                  setHoveredLink(null);
                  setTooltipPos(null);
                }}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => (
            <g key={node.id}>
              <rect
                x={node.x}
                y={node.y}
                width={NODE_WIDTH}
                height={node.height}
                rx={3}
                fill={glassSections ? 'rgb(34,211,238)' : node.color}
                fillOpacity={0.9}
                stroke={glassSections ? 'rgb(34,211,238)' : node.color}
                strokeWidth={0.5}
                strokeOpacity={0.5}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
              />
              {/* Label */}
              <text
                x={node.column === 2 ? node.x + NODE_WIDTH + 6 : node.x - 6}
                y={node.y + node.height / 2}
                textAnchor={node.column === 2 ? 'start' : 'end'}
                dominantBaseline="central"
                style={{
                  fontSize: 11,
                  fill: 'var(--dv-text-primary)',
                }}
              >
                {node.label}
              </text>
            </g>
          ))}

          {/* Tooltip */}
          {hoveredLink !== null && tooltipPos && (() => {
            const link = links[hoveredLink];
            const tooltipText = link.analyteName
              ? `${link.siteName} \u2192 ${link.wellName} \u2192 ${link.analyteName}: ${link.concentration.toFixed(4)} ${unitLabel}`
              : `${link.siteName} \u2192 ${link.wellName}: ${link.concentration.toFixed(4)} ${unitLabel}`;

            // Approximate text width
            const textWidth = tooltipText.length * 5.5 + 16;
            const tipX = Math.min(tooltipPos.x + 12, SVG_WIDTH - textWidth);
            const tipY = Math.max(tooltipPos.y - 30, 4);

            return (
              <g style={{ pointerEvents: 'none' }}>
                <rect
                  x={tipX}
                  y={tipY}
                  width={textWidth}
                  height={24}
                  rx={CHART_STYLES.tooltip.borderRadius}
                  fill="var(--dv-bg-card)"
                  stroke="var(--dv-border-subtle)"
                  strokeWidth={1}
                  fillOpacity={0.95}
                />
                <text
                  x={tipX + 8}
                  y={tipY + 15}
                  style={{
                    fontSize: CHART_STYLES.tooltip.fontSize,
                    fill: 'var(--dv-text-primary)',
                  }}
                >
                  {tooltipText}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
  );
}
